import { createSelector } from '@reduxjs/toolkit';

import { naturalSort } from '../utils/sortFunctions';

const selectActionPlanStatusDetails = (state) =>
    state.entities.directusService.actionPlanStatusDetails;

const selectAssetRequestStatusDetails = (state) =>
    state.entities.directusService.assetRequestStatusDetails;
const selectAssetRequestOrderTypeDetails = (state) =>
    state.entities.directusService.assetRequestOrderTypeDetails;

const selectAssetRequestAssetDetails = (state) =>
    state.entities.directusService.assetRequestAssetDetails;

const selectAssetRequestTypeDetails = (state) =>
    state.entities.directusService.assetRequestRequestTypeDetails;
const selectAttendanceDetails = (state) => state.entities.directusService.attendanceDetails;
const selectBarrierNameDetails = (state) => state.entities.directusService.barrierNameDetails;

const selectClaimEventTypes = (state) => state.entities.directusService.claimEventTypes;
const selectCommunicationOptOutTypeData = (state) =>
    state.entities.directusService.communicationOptOutTypes;
const selectContractEntries = (state) => state.entities.directusService.contractDetails;

const selectDocumentTypeEntries = (state) => state.entities.directusService.documentTypes;

const selectLocationIdsAndNamesData = (state) => state.entities.directusService.locationIdsAndNames;
const selectLocationForServicesData = (state) =>
    state.entities.directusService.locationsForServices;

const selectMaximumTravelDistanceDetails = (state) =>
    state.entities.directusService.maximumTravelDistanceDetails;

const selectParticipantStatusDetails = (state) =>
    state.entities.directusService.participantStatusDetails;
const selectPoolStatusDetails = (state) => state.entities.directusService.poolStatusDetails;
const selectServiceEntries = (state) => state.entities.directusService.serviceDetails;
const selectSmokingCoResultEntries = (state) => state.entities.directusService.smokingCoResults;

// EXPORTS
export const selectActionPlanOutcomes = createSelector(
    selectActionPlanStatusDetails,
    (actionPlanStatusDetails) => actionPlanStatusDetails
);

export const selectAttendedStatusId = createSelector(
    selectAttendanceDetails,
    (attendanceDetails) =>
        attendanceDetails.find((el) => el.name.toLowerCase() === 'attended')?.id || ''
);

export const selectFailedToAttendStatusId = createSelector(
    selectAttendanceDetails,
    (attendanceDetails) =>
        attendanceDetails?.find((el) => el.name.toLowerCase() === 'failed to attend')?.id || ''
);

export const selectNotKnownStatusId = createSelector(
    selectAttendanceDetails,
    (attendanceDetails) =>
        attendanceDetails.find((el) => el.name.toLowerCase() === 'not known')?.id || ''
);

export const selectBarrierDetails = createSelector(
    selectBarrierNameDetails,
    (barrierNameDetails) => {
        const newBarriers = [...barrierNameDetails];
        return newBarriers.sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
    }
);

export const selectAttachmentClaimTypeId = createSelector(
    selectClaimEventTypes,
    (claimEventTypes) =>
        claimEventTypes.find((el) => el.name.toLowerCase() === 'attachment')?.id || ''
);

export const selectContracts = createSelector(
    selectContractEntries,
    (contractDetails) => contractDetails
);

export const selectDocumentTypes = createSelector(selectDocumentTypeEntries, (documentTypes) => {
    const newDocumentTypes = documentTypes?.length > 0 ? [...documentTypes] : [];
    return newDocumentTypes.length > 0
        ? newDocumentTypes.sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
        : [];
});

export const selectLocationIdsAndNamesLength = createSelector(
    selectLocationIdsAndNamesData,
    (locationIdsAndNames) => locationIdsAndNames.length
);

export const selectLocationsForServices = createSelector(
    selectLocationForServicesData,
    (locationsForServices) => locationsForServices
);

export const selectPoolStatusesForAccepted = createSelector(
    selectPoolStatusDetails,
    (poolStatusDetails) => poolStatusDetails.filter((el) => el.name.toLowerCase() !== 'requested')
);

export const selectPoolStatusesForRejected = createSelector(
    selectPoolStatusDetails,
    (poolStatusDetails) =>
        poolStatusDetails.filter(
            (el) => el.name.toLowerCase() !== 'requested' && el.name.toLowerCase() !== 'accepted'
        )
);

export const selectServices = createSelector(
    selectServiceEntries,
    (serviceDetails) => serviceDetails
);

export const selectSmokingCoResults = createSelector(
    selectSmokingCoResultEntries,
    (smokingCoResults) => {
        const sortedArr = [...smokingCoResults];
        return sortedArr.sort((a, b) => a.name - b.name);
    }
);

export const selectAssetRequestStatus = createSelector(
    selectAssetRequestStatusDetails,
    (assetRequestStatusDetails) => assetRequestStatusDetails
);

export const selectAssetRequestOrderType = createSelector(
    selectAssetRequestOrderTypeDetails,
    (assetRequestOrderTypeDetails) => assetRequestOrderTypeDetails
);
export const selectAssetRequestAsset = createSelector(
    selectAssetRequestAssetDetails,
    (assetRequestAssetDetails) => assetRequestAssetDetails
);

export const selectAssetRequestType = createSelector(
    selectAssetRequestTypeDetails,
    (assetRequestTypeDetails) => assetRequestTypeDetails
);

export const selectCommunicationOptOutTypes = createSelector(
    selectCommunicationOptOutTypeData,
    (communicationOptOutTypes) => communicationOptOutTypes
);

export const selectSmsCommunicationOptOutTypeId = createSelector(
    selectCommunicationOptOutTypeData,
    (communicationOptOutTypes) => communicationOptOutTypes?.find((type) => type.name === 'SMS')?.id
);

export const selectEmailCommunicationOptOutTypeId = createSelector(
    selectCommunicationOptOutTypeData,
    (communicationOptOutTypes) =>
        communicationOptOutTypes?.find((type) => type.name === 'Email')?.id
);

export const selectPhoneCallsCommunicationOptOutTypeId = createSelector(
    selectCommunicationOptOutTypeData,
    (communicationOptOutTypes) =>
        communicationOptOutTypes?.find((type) => type.name === 'Phone Calls')?.id
);

export const selectMaximumTravelDistance = createSelector(
    selectMaximumTravelDistanceDetails,
    (maximumTravelDistanceDetails) => naturalSort(maximumTravelDistanceDetails, 'name')
);

export const selectParticipantStatuses = createSelector(
    selectParticipantStatusDetails,
    (participantStatusTypes) => participantStatusTypes
);

export const selectDoubtRaisedStatusId = createSelector(
    selectParticipantStatuses,
    (participantStatusTypes) =>
        participantStatusTypes?.find((el) => el.name.toLowerCase() === 'doubt raised')?.id || ''
);

export const selectSanctionedStatusId = createSelector(
    selectParticipantStatuses,
    (participantStatusTypes) =>
        participantStatusTypes?.find((el) => el.name.toLowerCase() === 'sanctioned')?.id || ''
);
